import { Component } from '@theme/component';

/**
 * Fits a desktop description to its paired media, leaving mobile content fully visible.
 * @extends {Component<{preview: HTMLElement, disclosure: HTMLDetailsElement, toggle: HTMLElement}>}
 */
class ProductDescription extends Component {
  requiredRefs = ['preview', 'disclosure', 'toggle'];
  #desktop = matchMedia('(min-width: 750px)');
  #controller;
  #resizeObserver = new ResizeObserver(() => this.#scheduleMeasure());
  #mediaObserver = new MutationObserver(() => this.#observeLayout());
  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    window.addEventListener('resize', this.#scheduleMeasure, { signal });
    this.#desktop.addEventListener('change', this.#scheduleMeasure, { signal });
    document.fonts?.addEventListener('loadingdone', this.#scheduleMeasure, { signal });
    this.#observeLayout();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#observeLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#resizeObserver.disconnect();
    this.#mediaObserver.disconnect();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #observeLayout() {
    this.#resizeObserver.disconnect();
    this.#mediaObserver.disconnect();
    const grid = this.closest('.product-information__grid');
    const media = grid?.querySelector('.product-information__media');
    const targets = [this.refs.preview.firstElementChild, this.closest('.product-details'), media];
    if (media) {
      targets.push(media.querySelector('media-gallery'), ...media.querySelectorAll('slideshow-slides, .media-gallery__grid'));
      this.#mediaObserver.observe(media, { childList: true, subtree: true });
    }
    for (const target of targets) {
      if (target) this.#resizeObserver.observe(target);
    }
    this.#scheduleMeasure();
  }

  #scheduleMeasure = () => {
    if (!this.isConnected || this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      this.#frame = 0;
      this.#measure();
    });
  };

  #measure() {
    const { preview, disclosure } = this.refs;
    let available = Number.parseFloat(getComputedStyle(this).getPropertyValue('--description-preview-height'));
    const toggleSpace = Number.parseFloat(getComputedStyle(this).getPropertyValue('--description-toggle-space')) || 60;
    available += toggleSpace;
    const grid = this.closest('.product-information__grid');
    if (grid) {
      const media = grid.querySelector('.product-information__media');
      const gallery = media?.querySelector('media-gallery');
      const details = this.closest('.product-details');
      const photo = [...(gallery?.querySelectorAll('slideshow-slides, .media-gallery__grid > .product-media-container') || [])]
        .find(element => element.getBoundingClientRect().height > 0);
      available = Infinity;
      if (media && gallery && details && photo) {
        const gridRect = grid.getBoundingClientRect();
        const mediaStyle = getComputedStyle(media);
        const galleryStyle = getComputedStyle(gallery);
        const detailsStyle = getComputedStyle(details);
        const mediaBottom = media.getBoundingClientRect().top - gridRect.top
          + (Number.parseFloat(mediaStyle.paddingTop) || 0)
          + (Number.parseFloat(galleryStyle.marginTop) || 0)
          + photo.getBoundingClientRect().bottom - gallery.getBoundingClientRect().top;
        const descriptionTop = this.getBoundingClientRect().top - details.getBoundingClientRect().top
          + (Number.parseFloat(detailsStyle.marginTop) || 0);
        available = Math.max(toggleSpace, mediaBottom - descriptionTop);
      }
    }
    const height = preview.firstElementChild?.getBoundingClientRect().height || 0;
    const overflow = this.#desktop.matches && height > available + 1;
    if (overflow) {
      this.style.setProperty('--description-visible-height', `${Math.max(0, available - toggleSpace)}px`);
      if (this.dataset.overflow !== 'true') disclosure.open = false;
    } else {
      disclosure.open = true;
      this.style.removeProperty('--description-visible-height');
    }
    this.dataset.overflow = String(overflow);
  }
}

if (!customElements.get('product-description')) {
  customElements.define('product-description', ProductDescription);
}
